import * as React from "react";
import { push } from "connected-react-router";
import {
  updateOrder,
  resetOrder,
  resetQuote,
  fetchQuoteAsync
} from "../actions";
import { connect } from "react-redux";
import styled from "styled-components";
import { Row, Col } from "antd";
import moment from "moment";

import { MainComponentContainer } from "../../../shared/MainComponentContainer";
import { EntryComponentContainer } from "../../../shared/EntryComponentContainer";
import RowWrapper from "../../../shared/RowWrapper";
import DayInput from "../../../shared/DayInput";
import { AddressInput } from "../../addresses/components/AddressInput";
import TimeWindowPicker from "../../../shared/TimeWindowPicker";
import { ExtrasPicker } from "../../../shared/ExtrasPicker";
import { PriceDisplay } from "../../../shared/PriceDisplay";
import { RootState } from "../../../store/rootReducer";
import { QuoteRequest } from "../../../lib/model/QuoteRequest";
import { BookDirectOrder } from "../model/BookDirectOrder";
import { fetchAddressesAsync } from "../../addresses/actions";
import { LoadInputList } from "./LoadInputList";
import { Load } from "../../../lib/model/Load";
import { AddressValidator } from "../../../lib/validators/AddressValidator";

const EntryHeadline = styled.span`
  color: #061a32;
  font-size: 14px;
  line-height: 17px;
  font-weight: 400;
`;

const Label = styled.span`
  color: #061a32;
  font-size: 11px;
  line-height: 32px;
  font-weight: 300;
`;

const DayPickerWrapper = styled(Row)`
  margin-bottom: -44px;

  @media only screen and (max-width: 576px) {
    margin-bottom: 0;
  }
`;

const mapStateToProps = (state: RootState) => ({
  order: state.bookDirect.order,
  isLoadingQuote: state.bookDirect.isLoadingQuote,
  quote: state.bookDirect.quote,
  addresses: state.addresses.addresses,
  isLoadingAddresses: state.addresses.isLoadingAddresses,
  error: state.bookDirect.quoteError
});

const dispatchProps = {
  fetchQuote: fetchQuoteAsync.request,
  push,
  updateOrder,
  resetOrder,
  resetQuote,
  fetchAddresses: fetchAddressesAsync.request
};

type Props = ReturnType<typeof mapStateToProps> & typeof dispatchProps;

class BookDirectContainer extends React.Component<Props> {
  render() {
    const { order, isLoadingQuote, quote, updateOrder, error } = this.props;
    return (
      <>
        <MainComponentContainer>
          <RowWrapper type="flex">
            <Col span={24}>
              <Row type="flex">
                <Col span={24}>
                  <EntryComponentContainer>
                    <DayPickerWrapper type="flex">
                      <Col span={24}>
                        <EntryHeadline>Datum</EntryHeadline>
                        <br />
                      </Col>
                      <Col span={24}>
                        <Label>* Tag der Fahrt</Label>
                      </Col>
                    </DayPickerWrapper>
                    <Row
                      type="flex"
                      justify="space-around"
                      style={{ marginBottom: "8px" }}
                    >
                      <Col span={10} md={10} sm={14} xs={18}>
                        <DayInput
                          value={order.date ? moment(order.date) : undefined}
                          onChange={value =>
                            updateOrder({
                              ...order,
                              date: value ? value.toISOString() : undefined
                            })
                          }
                        />
                      </Col>
                    </Row>
                  </EntryComponentContainer>
                </Col>
              </Row>
              <Row type="flex" gutter={21} style={{ marginBottom: 18 }}>
                <Col span={12} md={12} xs={24}>
                  <EntryComponentContainer
                    style={{ height: "100%", marginBottom: 0 }}
                  >
                    <EntryHeadline>Beladung</EntryHeadline>
                    <AddressInput
                      title="* Beladestelle"
                      value={order.startAddress}
                      onChange={value =>
                        updateOrder({ ...order, startAddress: value })
                      }
                    />
                    <div style={{ height: "21px" }} />
                    <TimeWindowPicker
                      value={order.startWindow}
                      onChange={value =>
                        updateOrder({ ...order, startWindow: value })
                      }
                    />
                  </EntryComponentContainer>
                </Col>
                <Col span={12} md={12} xs={24}>
                  <EntryComponentContainer
                    style={{ height: "100%", marginBottom: 0 }}
                  >
                    <EntryHeadline>Entladung</EntryHeadline>
                    <AddressInput
                      title="* Entladestelle"
                      value={order.endAddress}
                      onChange={value =>
                        updateOrder({ ...order, endAddress: value })
                      }
                    />
                    <div style={{ height: "21px" }} />
                    <TimeWindowPicker
                      value={order.endWindow}
                      onChange={value =>
                        updateOrder({ ...order, endWindow: value })
                      }
                    />
                  </EntryComponentContainer>
                </Col>
              </Row>
              <Row type="flex">
                <Col span={24}>
                  <LoadInputList
                    loads={order.loads}
                    onLoadsChange={loads => updateOrder({ ...order, loads })}
                  />
                </Col>
              </Row>
              <Row type="flex" gutter={21}>
                <Col span={12} md={12} xs={24}>
                  <EntryComponentContainer>
                    <EntryHeadline>Fahrzeugoptionen</EntryHeadline>
                    <ExtrasPicker
                      activeValues={order.extras}
                      onChange={value =>
                        updateOrder({ ...order, extras: value })
                      }
                    />
                  </EntryComponentContainer>
                </Col>
                <Col span={12} md={12} xs={24} style={{ display: "flex" }}>
                  <EntryComponentContainer>
                    <EntryHeadline>Festpreis</EntryHeadline>
                    <PriceDisplay
                      isLoading={isLoadingQuote}
                      quote={quote || undefined}
                      continueEnabled={
                        isValidOrder(order) && !!this.props.quote
                      }
                      onContinueClicked={this.handleContinueClicked}
                      error={error || undefined}
                    />
                  </EntryComponentContainer>
                </Col>
              </Row>
            </Col>
          </RowWrapper>
        </MainComponentContainer>
      </>
    );
  }

  componentDidMount = () => {
    this.props.fetchAddresses();
  };

  componentDidUpdate = (prevProps: Props, prevState: {}, snapshot: any) => {
    const order = this.props.order;
    if (isValidOrder(order)) {
      const prevOrder = prevProps.order;
      if (prevOrder !== order) {
        this.props.fetchQuote(QuoteRequest.requestForDirectOrder(order));
      }
    } else {
      this.props.resetQuote();
    }
  };
  handleContinueClicked = () => {
    this.props.push("/summary");
  };
}

const addressValidator = new AddressValidator();

const isValidOrder = (order: BookDirectOrder): boolean => {
  const loadsValid = order.loads.reduce(
    (acc, load) => acc && isValidLoad(load),
    true
  );
  const addressesValid = [order.startAddress, order.endAddress].reduce(
    (acc, state) => acc && addressValidator.isValidState(state),
    true
  );
  return !!order.date && loadsValid && addressesValid;
};

const isValidLoad = (load: Load): boolean => {
  return (
    load.count > 0 && load.height > 0 && load.width > 0 && load.totalWeight > 0
  );
};

export default connect(
  mapStateToProps,
  dispatchProps
)(BookDirectContainer);
